import DeleteIcon from '@mui/icons-material/Delete';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { UserSignature } from 'api';
import {
  Avatar,
  Backdrop,
  BaseTextFieldController,
  Form,
  FormCancelButton,
  FormGroup,
  FormSubmitButton,
  RichEditorController,
} from 'components';
import { ACHIEVEMENT_FORM_FIELDS_LENGTH } from 'constants/fieldsLength';
import { useWindowSize } from 'hooks';
import { textFieldStyles, textFieldMobileStyles, richEditorStyles } from 'styles';
import { AchievementFormValues } from 'types';
import { getFullName } from 'utils';

import { initialWindowSizeProps } from '../constants';
import { useAchievementForm } from './hooks';
import {
  DeleteButtonW,
  HeaderAuthorInfo,
  HeaderAuthorName,
  HeaderDateTime,
  HeaderLeft,
  HeaderW,
} from './styled';
import { avatarSx, titleFormGroupSx } from './styles';
import { descriptionFieldRules, titleFieldRules } from './validation';

interface AchievementFormProps {
  isLoading: boolean;
  defaultValues: AchievementFormValues;
  onSubmit: (data: AchievementFormValues) => void;
  handleRemoveClick?: () => void;
  mode?: 'create' | 'edit';
  author?: UserSignature;
  createdAt?: string;
  onClose?: () => void;
}

export function AchievementForm({
  isLoading: isAchievementMutationLoading,
  defaultValues,
  onSubmit,
  mode = 'create',
  author,
  createdAt,
  handleRemoveClick,
  onClose,
}: AchievementFormProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize(initialWindowSizeProps);

  const isEditMode = mode === 'edit';

  const navigate = useNavigate();
  const handleCancel = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  const { isSubmitting, isDirty, control, handleFormSubmit } = useAchievementForm({
    isEditMode,
    defaultValues,
    onSubmit,
  });

  const isSubmitButtonDisabled =
    (isEditMode && !isDirty) || isSubmitting || isAchievementMutationLoading;
  const formGroupMarginBottom = isMobile ? 16 : 24;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;

  return (
    <>
      {(isAchievementMutationLoading || isSubmitting) && <Backdrop />}

      <>
        <Form onSubmit={handleFormSubmit}>
          {isEditMode && author && (
            <HeaderW>
              <HeaderLeft>
                <Avatar
                  size={isMobile ? 'xs' : 's'}
                  userId={author.id}
                  filename={author.avatar?.small || ''}
                  wrapperSx={avatarSx}
                />
                <HeaderAuthorInfo>
                  <HeaderAuthorName>
                    {getFullName({ firstName: author.first_name, lastName: author.last_name })}
                  </HeaderAuthorName>
                  {createdAt && <HeaderDateTime>{createdAt}</HeaderDateTime>}
                </HeaderAuthorInfo>
              </HeaderLeft>

              {handleRemoveClick && (
                <DeleteButtonW
                  onClick={handleRemoveClick}
                  startIcon={<DeleteIcon />}
                  variant="outlined"
                >
                  {t('career.achievements.remove.removeButton')}
                </DeleteButtonW>
              )}
            </HeaderW>
          )}

          <FormGroup marginBottom={formGroupMarginBottom} sx={titleFormGroupSx(isMobile)}>
            <BaseTextFieldController
              name="title"
              control={control}
              placeholder={t('career.achievements.create.form.placeholders.title')}
              sx={{ width: '100%', ...fieldStyles }}
              maxLength={ACHIEVEMENT_FORM_FIELDS_LENGTH.title.max}
              rules={titleFieldRules}
            />
          </FormGroup>

          <FormGroup marginBottom={formGroupMarginBottom} row>
            <RichEditorController
              name="description"
              control={control}
              placeholder={t('career.achievements.create.form.placeholders.description')}
              maxLength={ACHIEVEMENT_FORM_FIELDS_LENGTH.description.max}
              rules={descriptionFieldRules}
              sx={richEditorStyles(isMobile)}
            />
          </FormGroup>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <FormCancelButton variant="outlined" onClick={handleCancel}>
              {t('common.cancel')}
            </FormCancelButton>
            <FormSubmitButton disabled={isSubmitButtonDisabled} type="submit">
              {isEditMode ? t('common.save') : t('common.create')}
            </FormSubmitButton>
          </div>
        </Form>
      </>
    </>
  );
}
